import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';

interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalRooms: number;
  activeRooms: number;
  totalGames: number;
  gamesInProgress: number;
}

interface User {
  _id: string;
  username: string;
  email: string;
  displayName: string;
  gamesPlayed: number;
  gamesWon: number;
  level: number;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

interface GameRoom {
  id: string;
  name: string;
  gameType: string;
  players: number;
  maxPlayers: number;
  status: string;
  createdAt: string;
  createdBy: string;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();

  // State
  const [currentTab, setCurrentTab] = useState<'dashboard' | 'users' | 'rooms' | 'system'>('dashboard');
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [rooms, setRooms] = useState<GameRoom[]>([]);
  const [loading, setLoading] = useState(false);

  // Check if user is admin (for now, just check if username is 'admin')
  const isAdmin = user?.username === 'admin' || user?.email?.includes('admin');

  useEffect(() => {
    if (isAdmin) {
      loadSystemStats();
    }
  }, [isAdmin]);

  const loadSystemStats = async () => {
    setLoading(true);
    try {
      // Mock data for now - in production, these would be real API calls
      setTimeout(() => {
        setSystemStats({
          totalUsers: 42,
          activeUsers: 7,
          totalRooms: 15,
          activeRooms: 3,
          totalGames: 156,
          gamesInProgress: 2
        });

        setUsers([
          {
            _id: '1',
            username: 'player1',
            email: 'player1@example.com',
            displayName: 'Player One',
            gamesPlayed: 25,
            gamesWon: 18,
            level: 5,
            isActive: true,
            createdAt: '2024-01-15T10:00:00Z',
            lastLoginAt: '2024-12-28T15:30:00Z'
          },
          {
            _id: '2',
            username: 'cardmaster',
            email: 'master@example.com',
            displayName: 'Card Master',
            gamesPlayed: 78,
            gamesWon: 45,
            level: 8,
            isActive: true,
            createdAt: '2024-01-10T08:00:00Z',
            lastLoginAt: '2024-12-28T12:00:00Z'
          },
          {
            _id: '3',
            username: 'newbie',
            email: 'newbie@example.com',
            displayName: 'New Player',
            gamesPlayed: 3,
            gamesWon: 1,
            level: 1,
            isActive: false,
            createdAt: '2024-12-20T14:00:00Z',
            lastLoginAt: '2024-12-25T10:00:00Z'
          }
        ]);

        setRooms([
          {
            id: 'room-1',
            name: 'Weekend Tournament',
            gameType: 'kadi-ni-tidi',
            players: 4,
            maxPlayers: 4,
            status: 'playing',
            createdAt: '2024-12-28T14:00:00Z',
            createdBy: 'player1'
          },
          {
            id: 'room-2',
            name: 'Casual Game',
            gameType: 'kadi-ni-tidi',
            players: 2,
            maxPlayers: 4,
            status: 'waiting',
            createdAt: '2024-12-28T15:00:00Z',
            createdBy: 'cardmaster'
          },
          {
            id: 'room-3',
            name: 'Practice Room',
            gameType: 'kadi-ni-tidi',
            players: 1,
            maxPlayers: 6,
            status: 'waiting',
            createdAt: '2024-12-28T15:30:00Z',
            createdBy: 'newbie'
          }
        ]);

        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error loading system stats:', error);
      showError('Load Failed', 'Failed to load system statistics');
      setLoading(false);
    }
  };

  const handleUserAction = async (userId: string, action: 'activate' | 'deactivate' | 'delete') => {
    try {
      // Mock action - in production, this would be a real API call
      showSuccess('Action Completed', `User ${action} action completed successfully`);
      
      if (action === 'delete') {
        setUsers(prev => prev.filter(u => u._id !== userId));
      } else {
        setUsers(prev => prev.map(u => 
          u._id === userId ? { ...u, isActive: action === 'activate' } : u
        ));
      }
    } catch (error) {
      showError('Action Failed', `Failed to ${action} user`);
    }
  };

  const handleRoomAction = async (roomId: string, action: 'close' | 'reset') => {
    try {
      // Mock action - in production, this would be a real API call
      showSuccess('Action Completed', `Room ${action} action completed successfully`);
      
      if (action === 'close') {
        setRooms(prev => prev.filter(r => r.id !== roomId));
      } else {
        setRooms(prev => prev.map(r => 
          r.id === roomId ? { ...r, players: 0, status: 'waiting' } : r
        ));
      }
    } catch (error) {
      showError('Action Failed', `Failed to ${action} room`);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-red-400 mb-4">Access Denied</h1>
          <p className="text-gray-300">You do not have administrator privileges.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-400">🛠️ Admin Panel</h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-300">Welcome, {user?.displayName || user?.username}</span>
            <button 
              onClick={() => window.location.href = '/dashboard'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Game
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="flex space-x-1 p-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'users', label: 'Users', icon: '👥' },
            { id: 'rooms', label: 'Rooms', icon: '🎯' },
            { id: 'system', label: 'System', icon: '⚙️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                currentTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Dashboard Tab */}
        {currentTab === 'dashboard' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">System Overview</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
              </div>
            ) : systemStats ? (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="text-2xl font-bold text-blue-400">{systemStats.totalUsers}</div>
                    <div className="text-sm text-gray-400">Total Users</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="text-2xl font-bold text-green-400">{systemStats.activeUsers}</div>
                    <div className="text-sm text-gray-400">Active Users</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="text-2xl font-bold text-purple-400">{systemStats.totalRooms}</div>
                    <div className="text-sm text-gray-400">Total Rooms</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="text-2xl font-bold text-yellow-400">{systemStats.activeRooms}</div>
                    <div className="text-sm text-gray-400">Active Rooms</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="text-2xl font-bold text-indigo-400">{systemStats.totalGames}</div>
                    <div className="text-sm text-gray-400">Total Games</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="text-2xl font-bold text-red-400">{systemStats.gamesInProgress}</div>
                    <div className="text-sm text-gray-400">Games In Progress</div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button 
                      onClick={loadSystemStats}
                      className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      🔄 Refresh Stats
                    </button>
                    <button className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700">
                      📢 Send Announcement
                    </button>
                    <button className="p-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                      🧹 Clear Cache
                    </button>
                    <button className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700">
                      🚨 Emergency Stop
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Users Tab */}
        {currentTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">User Management</h2>
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                + Add User
              </button>
            </div>

            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left">Username</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Level</th>
                      <th className="px-4 py-3 text-left">Games</th>
                      <th className="px-4 py-3 text-left">Win Rate</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Last Login</th>
                      <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {users.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-700">
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium">{user.username}</div>
                            <div className="text-sm text-gray-400">{user.displayName}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Level {user.level}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{user.gamesPlayed}</td>
                        <td className="px-4 py-3 text-sm">
                          {user.gamesPlayed > 0 ? Math.round((user.gamesWon / user.gamesPlayed) * 100) : 0}%
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleUserAction(user._id, user.isActive ? 'deactivate' : 'activate')}
                              className={`px-2 py-1 text-xs rounded ${
                                user.isActive 
                                  ? 'bg-yellow-600 hover:bg-yellow-700' 
                                  : 'bg-green-600 hover:bg-green-700'
                              }`}
                            >
                              {user.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button 
                              onClick={() => handleUserAction(user._id, 'delete')}
                              className="px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Rooms Tab */}
        {currentTab === 'rooms' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Room Management</h2>
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                + Create Room
              </button>
            </div>

            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left">Room Name</th>
                      <th className="px-4 py-3 text-left">Game Type</th>
                      <th className="px-4 py-3 text-left">Players</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Created By</th>
                      <th className="px-4 py-3 text-left">Created At</th>
                      <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {rooms.map((room) => (
                      <tr key={room.id} className="hover:bg-gray-700">
                        <td className="px-4 py-3 font-medium">{room.name}</td>
                        <td className="px-4 py-3 text-sm">{room.gameType}</td>
                        <td className="px-4 py-3 text-sm">{room.players}/{room.maxPlayers}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            room.status === 'playing' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {room.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{room.createdBy}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(room.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleRoomAction(room.id, 'reset')}
                              className="px-2 py-1 text-xs rounded bg-yellow-600 hover:bg-yellow-700"
                            >
                              Reset
                            </button>
                            <button 
                              onClick={() => handleRoomAction(room.id, 'close')}
                              className="px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-700"
                            >
                              Close
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* System Tab */}
        {currentTab === 'system' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">System Configuration</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Server Status */}
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <h3 className="text-lg font-semibold mb-4">Server Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Backend:</span>
                    <span className="text-green-400">🟢 Running</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Database:</span>
                    <span className="text-green-400">🟢 Connected</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Redis:</span>
                    <span className="text-green-400">🟢 Connected</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">WebSocket:</span>
                    <span className="text-green-400">🟢 Active</span>
                  </div>
                </div>
              </div>

              {/* System Settings */}
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <h3 className="text-lg font-semibold mb-4">System Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Maintenance Mode</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">New User Registration</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">AI Players</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Logs */}
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <h3 className="text-lg font-semibold mb-4">Recent System Logs</h3>
              <div className="bg-black p-4 rounded font-mono text-sm space-y-1 max-h-60 overflow-y-auto">
                <div className="text-green-400">[INFO] Server started on port 3001</div>
                <div className="text-blue-400">[INFO] Database connected successfully</div>
                <div className="text-green-400">[INFO] User player1 logged in</div>
                <div className="text-yellow-400">[WARN] High memory usage detected</div>
                <div className="text-green-400">[INFO] Game room created by cardmaster</div>
                <div className="text-blue-400">[INFO] WebSocket connection established</div>
                <div className="text-green-400">[INFO] Game completed in room-1</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;